import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { setSelectedCenter, selectSelectedCenter } from '../../../services/ConfigurationService';
import { useGetMyProfileQuery } from '../../../services/ProfileService';

/**
 * The queue's center scope, two-way with the app-wide center (`state.center`, the NavBar's
 * top-left label): the `?center=` search param is authoritative when present (so the detail-page
 * round trip restores it) and pushes into the global slice; when absent, the page seeds from the
 * global center if accessible, else the first accessible center. Initialization is owned here —
 * not in `CenterSelect` — so the two writers can't race.
 */
export const useQueueCenter = (): { center: string; handleCenterChange: (code: string) => void } => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const globalCenter = useAppSelector(selectSelectedCenter);
  const { data: profile } = useGetMyProfileQuery();
  const center = searchParams.get('center') ?? '';

  const handleCenterChange = (code: string): void => {
    const next = new URLSearchParams(searchParams);
    if (code)
      next.set('center', code);
    else
      next.delete('center');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (center) {
      if (center !== globalCenter)
        dispatch(setSelectedCenter(center));
      return;
    }
    const accessible = profile?.centerAccess?.map((c) => c.code) ?? [];
    if (!accessible.length)
      return;
    handleCenterChange(accessible.includes(globalCenter) ? globalCenter : accessible[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleCenterChange is stable per searchParams, already covered by `center`
  }, [center, globalCenter, profile, dispatch]);

  return { center, handleCenterChange };
};
